import React, { useState } from 'react';
import { Entry, Header, QueryString, Cookie, Timings, PostData, KeyValueItem } from '../types';
import { formatBytes, formatMilliseconds, prettifyJson } from '../utils/formatters';
import { ChevronDownIcon, ChevronRightIcon } from './Icons';

interface CollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ title, children, defaultOpen = false }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="mb-3 bg-white rounded-lg shadow">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center p-3 text-left font-semibold text-gray-700 hover:bg-gray-50 transition-colors duration-200 rounded-t-lg"
        aria-expanded={isOpen}
      >
        <span>{title}</span>
        {isOpen ? <ChevronDownIcon className="h-5 w-5" /> : <ChevronRightIcon className="h-5 w-5" />}
      </button>
      {isOpen && (
        <div className="p-3 border-t border-gray-200">
          {children}
        </div>
      )}
    </div>
  );
};

interface KeyValueTableProps {
  data: KeyValueItem[] | Header[] | QueryString[] | Cookie[];
  title?: string;
  emptyText?: string;
}

const KeyValueTable: React.FC<KeyValueTableProps> = ({ data, title, emptyText = "No data available." }) => {
  if (!data || data.length === 0) {
    return <p className="text-sm text-gray-500">{emptyText}</p>;
  }
  return (
    <>
      {title && <h4 className="text-md font-semibold text-gray-700 mb-1 mt-2">{title}</h4>}
      <div className="overflow-x-auto">
        <table className="w-full text-sm text-left text-gray-700 font-mono">
          <thead className="text-xs text-gray-600 uppercase bg-gray-100">
            <tr>
              <th scope="col" className="py-2 px-3">Name</th>
              <th scope="col" className="py-2 px-3">Value</th>
            </tr>
          </thead>
          <tbody>
            {data.map((item, index) => (
              <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
                <td className="py-2 px-3 font-medium whitespace-nowrap break-all">{item.name}</td>
                <td className="py-2 px-3 whitespace-pre-wrap">{'value' in item ? String(item.value) : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};


interface BodyViewerProps {
  mimeType?: string;
  text?: string;
  encoding?: string;
  title: string;
}

const BodyViewer: React.FC<BodyViewerProps> = ({ mimeType, text, encoding, title }) => {
  const isJson = mimeType?.includes('json');
  let bodyContent: React.ReactNode;

  if (text) {
    if (encoding === 'base64') {
      if (mimeType === 'text/html') {
        // Decode base64 HTML content
        try {
          const decodedHtml = atob(text);
          bodyContent = (
            <div className="bg-gray-100 text-gray-800 p-2 rounded">
              <p className="text-xs text-gray-500 mb-1">(Decoded HTML content from Base64)</p>
              <div className="max-h-[400px] overflow-auto">
                <pre className="whitespace-pre-wrap break-all text-sm font-mono">{decodedHtml}</pre>
              </div>
            </div>
          );
        } catch (error) {
          bodyContent = (
            <>
              <p className="text-xs text-gray-500 mb-1">(Content is Base64 encoded - failed to decode HTML)</p>
              <div className="max-h-[400px] overflow-auto">
                <pre className="whitespace-pre-wrap break-all bg-gray-100 text-gray-800 p-2 rounded text-sm font-mono">{text}</pre>
              </div>
            </>
          );
        }
      } else {
        bodyContent = (
          <>
            <p className="text-xs text-gray-500 mb-1">(Content is Base64 encoded)</p>
            <div className="max-h-[400px] overflow-auto">
              <pre className="whitespace-pre-wrap break-all bg-gray-100 text-gray-800 p-2 rounded text-sm font-mono">{text}</pre>
            </div>
          </>
        );
      }
    } else if (isJson) {
      bodyContent = (
        <div className="max-h-[400px] overflow-auto">
          <pre className="whitespace-pre-wrap break-all bg-gray-100 text-gray-800 p-2 rounded text-sm font-mono">{prettifyJson(text)}</pre>
        </div>
      );
    } else if (mimeType?.startsWith('image/')) {
       bodyContent = <p className="text-sm text-gray-500">Image content (preview not available). Type: {mimeType}</p>;
    } else if (mimeType === 'text/html') {
      bodyContent = (
        <div className="bg-gray-100 text-gray-800 p-2 rounded">
          <p className="text-xs text-gray-500 mb-1">(HTML content)</p>
          <div className="max-h-[400px] overflow-auto">
            <pre className="whitespace-pre-wrap break-all text-sm font-mono">{text}</pre>
          </div>
        </div>
      );
    } else {
      bodyContent = (
        <div className="max-h-[400px] overflow-auto">
          <pre className="whitespace-pre-wrap break-all bg-gray-100 text-gray-800 p-2 rounded text-sm font-mono">{text}</pre>
        </div>
      );
    }
  } else {
    bodyContent = <p className="text-sm text-gray-500">No body content.</p>;
  }

  return (
    <CollapsibleSection title={title} defaultOpen={!!text}>
      {mimeType && <p className="text-xs text-gray-500 mb-2">MIME Type: {mimeType}</p>}
      {bodyContent}
    </CollapsibleSection>
  );
};

const TimingsBreakdown: React.FC<{ timings: Timings }> = ({ timings }) => {
  const timingData: KeyValueItem[] = Object.entries(timings)
    .filter(([, value]) => typeof value === 'number' && value >= 0) 
    .map(([key, value]) => ({
      name: key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
      value: formatMilliseconds(value as number),
    }));

  const totalTimeSum = Object.values(timings)
    .filter(v => typeof v === 'number' && v > 0)
    .reduce((sum, current) => sum + (current as number), 0);
    
  return (
    <div>
      <KeyValueTable data={timingData} emptyText="No timing details available." />
      {totalTimeSum > 0 && ( // Use totalTimeSum derived from raw positive timings for chart rendering logic
        <div className="mt-3 space-y-1">
          {timingData.map(item => {
            const valueMs = parseFloat(String(item.value).replace(/[^\d.-]/g, '')); // Extract number from "X ms" or "Y s"
            if (isNaN(valueMs) || valueMs <=0) return null;
            
            // Convert item.value to ms if it's in seconds for percentage calculation
            let valueInMsForCalc = valueMs;
            if (String(item.value).includes(' s')) {
                valueInMsForCalc = valueMs * 1000;
            }

            // Calculate percentage based on totalTimeSum (which is in ms)
            const percentage = totalTimeSum > 0 ? (valueInMsForCalc / totalTimeSum) * 100 : 0;
            if (percentage <= 0) return null; // Don't render if percentage is 0 or less

            return (
              <div key={item.name} className="flex items-center text-xs">
                <span className="w-1/3 truncate pr-2 text-gray-700" title={item.name as string}>{item.name}</span>
                <div className="w-2/3 bg-gray-300 rounded h-4">
                  <div
                    className="bg-violet-500 h-4 rounded text-right pr-1 text-white text-[10px] leading-4"
                    style={{ width: `${Math.max(0, Math.min(100,percentage))}%` }} // Ensure width is between 0 and 100
                    title={`${item.value}`}
                  >
                   {percentage > 10 ? item.value : ''}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


const RequestDetailView: React.FC<{ entry: Entry }> = ({ entry }) => {
  const { request, response, timings, time, serverIPAddress, startedDateTime } = entry;

  const generalInfo: KeyValueItem[] = [
    { name: 'URL', value: request.url },
    { name: 'Method', value: request.method },
    { name: 'HTTP Version', value: request.httpVersion },
    { name: 'Status', value: `${response.status} ${response.statusText}` },
    { name: 'Total Time', value: formatMilliseconds(time) },
    { name: 'Started At', value: new Date(startedDateTime).toLocaleString() },
    { name: 'Server IP', value: serverIPAddress || 'N/A' },
    { name: 'Request Headers Size', value: formatBytes(request.headersSize) },
    { name: 'Request Body Size', value: formatBytes(request.bodySize) },
    { name: 'Response Headers Size', value: formatBytes(response.headersSize) },
    { name: 'Response Body Size', value: formatBytes(response.content.size) },
     { name: 'Response Transfer Size (approx)', value: response._transferSize ? formatBytes(response._transferSize) : 'N/A' },
  ];

  return (
    <div className="text-gray-800">
      <span className="mb-1 text-xl font-bold text-violet-600 break-all">
        {request.method} {request.url}
      </span>
      <p className={`text-lg mb-4 font-semibold ${response.status >= 400 ? 'text-red-500' : 'text-green-500'}`}>
        {response.status} {response.statusText}
      </p>

      <CollapsibleSection title="General Information" defaultOpen={true}>
        <KeyValueTable data={generalInfo} />
      </CollapsibleSection>

      <CollapsibleSection title="Request Headers">
        <KeyValueTable data={request.headers} emptyText="No request headers."/>
      </CollapsibleSection>

      {request.queryString && request.queryString.length > 0 && (
        <CollapsibleSection title="Query String Parameters">
          <KeyValueTable data={request.queryString} emptyText="No query string parameters."/>
        </CollapsibleSection>
      )}

      {request.postData && (
        <BodyViewer 
          mimeType={request.postData.mimeType} 
          text={request.postData.text} 
          encoding={request.postData.encoding}
          title="Request Body" 
        />
      )}

      <CollapsibleSection title="Response Headers">
        <KeyValueTable data={response.headers} emptyText="No response headers."/>
      </CollapsibleSection>

      <BodyViewer 
        mimeType={response.content.mimeType} 
        text={response.content.text} 
        encoding={response.content.encoding}
        title="Response Body"
      />
      
      <CollapsibleSection title="Timings">
        <TimingsBreakdown timings={timings} />
      </CollapsibleSection>

      {request.cookies && request.cookies.length > 0 && (
        <CollapsibleSection title="Request Cookies">
          <KeyValueTable data={request.cookies.map(c => ({name: c.name, value: c.value}))} emptyText="No request cookies."/>
        </CollapsibleSection>
      )}

      {response.cookies && response.cookies.length > 0 && (
        <CollapsibleSection title="Response Cookies (Set-Cookie)">
           <KeyValueTable data={response.cookies.map(c => ({name: c.name, value: c.value}))} emptyText="No cookies set by response."/>
        </CollapsibleSection>
      )}
    </div>
  );
};

export default RequestDetailView;